/**
 * Guide step 5: the AI leasing agent (BR-AGENT-POLICY-SCOPE). Two explicit choices, each captioned with
 * where its current value comes from. The step's "Save and continue" (test id
 * `listing-agent-policy-save`, rendered by the guide shell) saves both at listing scope, so the agent
 * row counts as confirmed rather than merely defaulted.
 */

import { useQuery } from '@tanstack/react-query';
import { Button } from '../../../../components/ui/button';
import { Skeleton } from '../../../../components/ui/skeleton';
import { fetchListing } from '../../../../lib/leasing.api';
import { AgentPolicyEditor } from '../../listing-detail/AgentPolicyEditor';
import type { GuideStepProps } from '../guide-steps';

export function AgentStep({ listing, handleRef, onDirtyChange, onSavingChange }: GuideStepProps) {
  const listingId = listing?.id ?? '';
  // canManage comes from the same detail query the listing page reads (permissions are per caller).
  const detail = useQuery({
    queryKey: ['listing', listingId],
    queryFn: () => fetchListing(listingId),
    enabled: listingId !== '',
  });

  if (!listing) return null;

  return (
    <div data-testid="guide-step-agent" className="flex flex-col gap-6">
      <header>
        <h1 className="text-2xl font-semibold text-foreground" data-testid="guide-step-heading">
          AI leasing agent
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          If you turn it on, the agent answers prospect questions about this listing once it is live.
        </p>
      </header>

      {detail.isPending ? (
        <Skeleton className="h-40 w-full" data-testid="guide-step-agent-loading" />
      ) : detail.isError ? (
        <div
          role="alert"
          data-testid="guide-step-agent-error"
          className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive"
        >
          <p>Could not load this listing.</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-2 min-h-[44px]"
            data-testid="guide-step-agent-retry"
            onClick={() => void detail.refetch()}
          >
            Retry
          </Button>
        </div>
      ) : (
        <AgentPolicyEditor
          listingId={listing.id}
          canManage={detail.data.permissions.canManage}
          isDraft={listing.status === 'draft'}
          embedded
          handleRef={handleRef}
          onDirtyChange={onDirtyChange}
          onSavingChange={onSavingChange}
        />
      )}
    </div>
  );
}
